import React from 'react';
import { Link } from 'react-router-dom';
import buildingImg from '../assets/building.png';
import SEO from '../components/SEO';

const Services = () => {
  // Service Data Array for clean mapping
  const services = [
    { icon: 'home', title: 'PROPERTY BUYING', desc: 'We help you find the right property that matches your lifestyle and investment goals.' },
    { icon: 'sell', title: 'PROPERTY SELLING', desc: 'Get the best value for your property with our expert marketing and client network.' },
    { icon: 'support_agent', title: 'REAL ESTATE CONSULTING', desc: 'Strategic advice and market insights to help you make informed real estate decisions.' },
    { icon: 'trending_up', title: 'INVESTMENT ADVISORY', desc: 'Maximize returns with our data-driven investment strategies and portfolio recommendations.' },
    { icon: 'campaign', title: 'PROJECT MARKETING', desc: 'Premium marketing solutions for developers to launch and sell projects successfully.' },
    { icon: 'location_city', title: 'COMMERCIAL & RESIDENTIAL PROPERTIES', desc: 'Wide range of commercial and residential properties across prime locations.' },
    { icon: 'map', title: 'LAND ACQUISITION', desc: 'We help you acquire prime land with clear titles and high growth potential.' },
    { icon: 'manage_accounts', title: 'PROPERTY MANAGEMENT', desc: 'Comprehensive management services ensuring your asset\'s value and tenant satisfaction.' },
  ];

  // Process Steps Data
  const processes = [
    { icon: 'search', title: 'DISCOVER', desc: 'We understand your needs and identify the best opportunities.' },
    { icon: 'assignment', title: 'PLAN', desc: 'Expert analysis and planning to create the right real estate strategy.' },
    { icon: 'handshake', title: 'EXECUTE', desc: 'We execute the plan with precision and complete transparency.' },
    { icon: 'key', title: 'DELIVER', desc: 'Timely delivery and continuous support even after possession.' },
    { icon: 'verified_user', title: 'SUPPORT', desc: 'Relationship beyond transactions with trust and commitment.' },
  ];

  return (
    <div className="bg-[#041424] min-h-screen w-full font-sans">
      <SEO 
        title="Premium Real Estate Services | HBB0011 REALTY"
        description="From property management to corporate architectural consulting, explore the bespoke services offered by HBB0011 REALTY."
        keywords="real estate services, property management, architectural consulting, luxury real estate services"
      />
      
      {/* 1. Hero Section */}
      <header className="relative w-full pt-28 pb-16 md:pt-32 md:pb-24 lg:min-h-[75vh] flex items-center overflow-hidden">
        {/* Background Image Blend */}
        <div className="absolute inset-0 z-0 flex justify-end">
          <div className="w-full lg:w-1/2 h-full relative">
            <img 
              src={buildingImg} 
              alt="Luxury real estate services" 
              className="w-full h-full object-cover opacity-30 lg:opacity-60 mix-blend-luminosity"
            />
            {/* Gradients to fade image into the navy background smoothly */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#041424] via-[#041424]/80 lg:via-[#041424]/20 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#041424] via-transparent to-transparent"></div>
          </div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full lg:w-3/5 space-y-4 sm:space-y-6 text-center lg:text-left">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center lg:justify-start gap-2 text-xs sm:text-sm font-bold tracking-widest mb-2 sm:mb-4">
              <Link to="/" className="text-white hover:text-[#D4AF37] transition-colors uppercase">Home</Link>
              <span className="text-gray-400">&gt;</span>
              <span className="text-[#D4AF37] uppercase">Services</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-2 tracking-tight">
              OUR SERVICES
            </h1>
            
            <div className="w-16 sm:w-24 h-1 bg-[#D4AF37] mx-auto lg:mx-0 my-4 sm:my-6"></div>

            <h2 className="text-xl sm:text-2xl md:text-3xl text-white font-light leading-tight mb-4 sm:mb-6">
              End-to-End Real Estate Solutions <br className="hidden sm:block"/>
              Tailored to Your Needs
            </h2>
            <p className="text-gray-400 max-w-lg mx-auto lg:mx-0 text-sm sm:text-base leading-relaxed">
              At HBB0011 Realty Realtors Pvt. Ltd., we provide comprehensive real estate services that ensure value, transparency, and a seamless experience for every client.
            </p>
          </div>
        </div>
      </header>

      {/* 2. Services Grid */}
      <section className="w-full bg-[#041424] pb-24 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {services.map((service, index) => (
              <div key={index} className="group relative p-8 border border-gray-800 rounded-sm hover:border-[#D4AF37]/50 bg-[#041424] hover:bg-[#062038] transition-all duration-300 flex flex-col items-center text-center shadow-lg">
                <div className="w-16 h-16 mb-6 rounded-full border border-gray-700 group-hover:border-[#D4AF37] flex items-center justify-center transition-colors duration-300">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">{service.icon}</span>
                </div>
                <h3 className="text-white text-sm font-bold tracking-widest uppercase mb-4">{service.title}</h3>
                <p className="text-gray-400 text-xs leading-relaxed mb-8 flex-grow">{service.desc}</p>
                <Link to="#" className="text-[#D4AF37] text-[10px] sm:text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:text-white transition-colors mt-auto">
                  Read More <span className="material-symbols-outlined text-sm">arrow_forward</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Commitment & Stats Section (No Photo layout) */}
      <section className="py-20 border-t border-gray-800 bg-[#041424]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left: Commitment (Adjusted without photo) */}
            <div className="relative">
              <h4 className="text-[#D4AF37] uppercase text-[10px] sm:text-xs font-bold tracking-widest mb-4">Our Commitment</h4>
              <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light mb-6 leading-tight">
                Delivering Excellence.<br/>
                <span className="font-bold">Building Relationships.</span>
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-10 max-w-md">
                We are committed to delivering exceptional real estate solutions with integrity, transparency, and professionalism. Our client-first approach has earned us the trust of hundreds of satisfied clients across India.
              </p>
              
              <div className="mt-8">
                <div className="inline-block relative mb-2">
                  <span className="text-xl sm:text-2xl font-sans font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-yellow-400 uppercase tracking-widest relative z-10">HABEEB RAHIMAN M</span>
                  <div className="absolute -bottom-1 left-0 w-1/3 h-[2px] bg-[#D4AF37]"></div>
                </div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Founder & CEO</p>
              </div>
            </div>

            {/* Right: Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">military_tech</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">1+</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Years of Experience</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">domain</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">1+</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Projects Delivered</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">groups</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">10+</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Happy Clients</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">location_on</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">5+</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">States Covered</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">currency_rupee</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">2+ <span className="text-lg">Cr</span></h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Total Transaction Value</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="material-symbols-outlined text-[#D4AF37] text-4xl font-light">star</span>
                <div>
                  <h3 className="text-3xl font-bold text-white">100%</h3>
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">Client Satisfaction</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. Our Process Section */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-[#D4AF37] uppercase text-[10px] sm:text-xs font-bold tracking-widest mb-2">Our Process</h4>
            <h2 className="text-3xl sm:text-4xl text-[#041424] font-bold">Simple Steps. Seamless Experience.</h2>
          </div>

          <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start relative gap-12 lg:gap-0">
            {/* Hidden connecting line for desktop only */}
            <div className="hidden lg:block absolute top-10 left-[10%] w-[80%] h-[1px] border-t-2 border-dashed border-gray-300 z-0"></div>

            {processes.map((step, index) => (
              <div key={index} className="relative z-10 flex flex-col items-center text-center w-full lg:w-1/5 px-4">
                <div className="w-20 h-20 rounded-full bg-[#041424] border-4 border-white shadow-xl flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-[#D4AF37] text-3xl font-light">{step.icon}</span>
                </div>
                <h3 className="text-[#041424] font-bold text-sm uppercase tracking-widest mb-3">{step.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[200px]">{step.desc}</p>
                
                {/* Mobile only connecting arrow */}
                {index !== processes.length - 1 && (
                  <span className="material-symbols-outlined text-gray-300 text-3xl mt-8 lg:hidden">arrow_downward</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Call to Action */}
      <section className="bg-[#041424] py-12 sm:py-16 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
          
          <div className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 sm:gap-6">
            <div className="hidden sm:flex w-14 sm:w-16 h-14 sm:h-16 shrink-0 rounded-sm border border-[#D4AF37]/30 items-center justify-center text-[#D4AF37]">
              <span className="material-symbols-outlined text-3xl sm:text-4xl font-light">headset_mic</span>
            </div>
            <div>
              <h3 className="text-2xl sm:text-3xl text-white font-light mb-2">
                Let's Find the Right Real Estate Solution for You
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm">
                Connect with our experts today for personalized assistance.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto shrink-0">
            <button className="bg-[#D4AF37] text-[#041424] px-4 sm:px-6 py-3 sm:py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Talk to an Expert
              <span className="material-symbols-outlined text-sm sm:text-base">arrow_forward</span>
            </button>
            <button className="border border-white text-white px-4 sm:px-6 py-3 sm:py-4 rounded-sm font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-white hover:text-[#041424] transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
              Download Brochure
              <span className="material-symbols-outlined text-sm sm:text-base">download</span>
            </button>
          </div>

        </div>
      </section>

    </div>
  );
};

export default Services;